import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

// Import the specialized stores
import { useDataCollectionStore } from './data-collection.js'
import { useDataReviewStore } from './data-review.js'
import { useAiOverviewStore } from './ai-overview.js'

export const useResearchStore = defineStore('research', () => {
  // Research store's own state - shared loading/error across all research operations
  const questions = ref([])
  const loading = ref(false)
  const error = ref(null)
  
  // Track collection start status per session to prevent expensive re-runs
  const sessionCollectionStatus = ref({}) // { [sessionId]: { hasStarted: boolean, startedAt: Date } }
  
  // Initialize specialized stores directly (safe since they don't import research store)
  const dataCollectionStore = useDataCollectionStore()
  const dataReviewStore = useDataReviewStore()  
  const aiOverviewStore = useAiOverviewStore()

  // Create reactive computed properties for cross-store reactivity
  const dataReview = computed(() => dataReviewStore.reviewData)
  const collectionResults = computed(() => dataCollectionStore.collectionResults)

  // Collection tracking functions
  function markCollectionStarted(sessionId) {
    if (!sessionId) return
    sessionCollectionStatus.value[sessionId] = {
      hasStarted: true,
      startedAt: new Date()
    }
  }

  function hasCollectionStarted(sessionId) {
    if (!sessionId) return false
    return sessionCollectionStatus.value[sessionId]?.hasStarted || false
  }

  function resetCollectionStatus(sessionId) {
    if (!sessionId) return
    delete sessionCollectionStatus.value[sessionId]
  }

  return {
    // === CORE RESEARCH STATE ===
    // Essential state kept in main store - shared across all research operations
    questions,
    loading,
    error,
    
    // === DATA COLLECTION STORE PROXIES ===
    // State from data collection store with proper reactivity
    collectionResults,
    
    // Methods proxied to data collection store with proper context
    setCollectionResults: (...args) => dataCollectionStore.setCollectionResults(...args),
    getCollectionResults: (...args) => dataCollectionStore.getCollectionResults(...args),
    clearCollectionResults: (...args) => dataCollectionStore.clearCollectionResults(...args),
    getDataCollectionCategories: (...args) => dataCollectionStore.getDataCollectionCategories(...args),
    startDataCollection: (...args) => dataCollectionStore.startDataCollection(...args),
    getCollectionStatus: (...args) => dataCollectionStore.getCollectionStatus(...args),
    getSessionCollectionStatus: (...args) => dataCollectionStore.getSessionCollectionStatus(...args),
    getLatestCollectionResults: (...args) => dataCollectionStore.getLatestCollectionResults(...args),
    
    // Aliased methods for backward compatibility
    getResultsByState: (...args) => dataCollectionStore.getCollectionResultsByState(...args),
    getCollectionResultsWithArticles: (...args) => dataCollectionStore.getCollectionResultsWithArticles(...args),
    getCategoryResults: (...args) => dataCollectionStore.getCategoryCollectionResults(...args),
    
    // State checker utilities
    isSessionReady: (...args) => dataCollectionStore.isSessionReady(...args),
    hasCollectionResults: (...args) => dataCollectionStore.hasCollectionResults(...args),
    needsDisambiguation: (...args) => dataCollectionStore.needsDisambiguation(...args),
    isCollectionInProgress: (...args) => dataCollectionStore.isCollectionInProgress(...args),
    getSessionStateInfo: (...args) => dataCollectionStore.getSessionStateInfo(...args),
    
    // Category update methods
    getCategoryUpdateStatus: (...args) => dataCollectionStore.getCategoryUpdateStatus(...args),
    previewCategoryUpdates: (...args) => dataCollectionStore.previewCategoryUpdates(...args),
    executeSelectiveUpdate: (...args) => dataCollectionStore.executeSelectiveUpdate(...args),

    // === DATA REVIEW STORE PROXIES ===
    // All data review functionality now properly delegated to dataReviewStore
    // Use reactive computed references for proper reactivity
    dataReview,
    get dataReviewLoading() { return loading.value }, // Use research store loading
    get dataReviewError() { return error.value }, // Use research store error
    get approvalLoading() { return loading.value }, // Alias for compatibility
    get approvalError() { return error.value }, // Alias for compatibility
    
    // Computed properties properly delegated with reactivity - return the computed refs directly
    reviewArticles: computed(() => dataReviewStore.reviewArticles),
    reviewPagination: computed(() => dataReviewStore.reviewPagination),
    reviewOverview: computed(() => dataReviewStore.reviewOverview),
    qualityMetrics: computed(() => dataReviewStore.qualityMetrics),
    sourceAnalysis: computed(() => dataReviewStore.sourceAnalysis),
    categoryBreakdown: computed(() => dataReviewStore.categoryBreakdown),
    reviewOptions: computed(() => dataReviewStore.reviewOptions),
    collectionMetadata: computed(() => dataReviewStore.collectionMetadata),
    reviewEntityRecords: computed(() => dataReviewStore.reviewEntityRecords),
    entityRecordsCount: computed(() => dataReviewStore.entityRecordsCount),
    reviewFallback: computed(() => dataReviewStore.reviewFallback),
    
    // All data review methods properly delegated with correct context
    getDataCollectionReview: (...args) => dataReviewStore.getDataCollectionReview(...args),
    getReviewSummary: (...args) => dataReviewStore.getReviewSummary(...args),
    updateReviewStatus: (...args) => dataReviewStore.updateReviewStatus(...args),
    bulkUpdateReviewStatus: (...args) => dataReviewStore.bulkUpdateReviewStatus(...args),
    getReviewItems: (...args) => dataReviewStore.getReviewItems(...args),
    exportReviewData: (...args) => dataReviewStore.exportReviewData(...args),
    getReviewStatistics: (...args) => dataReviewStore.getReviewStatistics(...args),
    rejectArticle: (...args) => dataReviewStore.rejectArticle(...args),
    approveArticle: (...args) => dataReviewStore.approveArticle(...args),
    bulkRejectArticles: (...args) => dataReviewStore.bulkRejectArticles(...args),
    bulkApproveArticles: (...args) => dataReviewStore.bulkApproveArticles(...args),

    // === AI OVERVIEW STORE PROXIES ===
    // AI overview methods with proper context
    getAiOverview: (...args) => aiOverviewStore.getLatestAiOverview(...args),
    generateAiOverview: (...args) => aiOverviewStore.generateAiOverview(...args),
    deleteAiOverview: (...args) => aiOverviewStore.deleteAiOverview(...args),
    regenerateAiOverview: (...args) => aiOverviewStore.regenerateAiOverview(...args),
    getAiOverviewStatus: (...args) => aiOverviewStore.getAiOverviewStatus(...args),
    getAiOverviewHistory: (...args) => aiOverviewStore.getAiOverviewHistory(...args),
    exportAiOverview: (...args) => aiOverviewStore.exportAiOverview(...args),

    // === COLLECTION TRACKING ===
    // Track collection start status to prevent expensive re-runs
    sessionCollectionStatus,
    markCollectionStarted,
    hasCollectionStarted,
    resetCollectionStatus,

    // === DIRECT STORE ACCESS ===
    // Expose specialized stores for direct access in new code
    $dataCollection: dataCollectionStore,
    $dataReview: dataReviewStore,
    $aiOverview: aiOverviewStore
  }
})